// Safely renders arbitrary HTML in React.
// Runs normal <script> tags once; defers embeds (HubSpot, Typeform, Calendly) to utils for SPA-safe mounting.

import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import HtmlReactParser from "html-react-parser"
import Box from "../Box"
import { renderAllEmbeds, isEmbedScript } from "./utils"

const createScriptElement = domNode => {
  // console.dir(domNode, { depth: null })
  const script = document.createElement("script")

  // add all attributes
  Object.keys(domNode.attribs).forEach(attrib => {
    script[attrib] = domNode.attribs[attrib]
  })

  domNode.children.forEach(child => {
    if (child.data) {
      const inlineScript = document.createTextNode(child.data)
      script.appendChild(inlineScript)
    }
  })
  return script
}

const CustomHtml = ({ html, ...rest }) => {
  const ref = useRef(null)
  const [scriptsInjected, setScriptsInjected] = useState(false) // state to keep track if the script was injected

  // On first render only, parse and inject non-embed <script> tags in order.
  useEffect(() => {
    if (!html || !ref.current || scriptsInjected) return
    const scripts = []
    HtmlReactParser(html, {
      replace(domNode) {
        if (domNode.type === "script" && !isEmbedScript(domNode.attribs?.src)) {
          let script = createScriptElement(domNode)
          scripts.push(script)
        }
      },
    })

    if (scripts.length > 0) {
      // Make sure scripts run in order by appending the next script
      // on the previous script's "onload" function
      scripts.forEach((script, i) => {
        if (i + 1 < scripts.length) {
          script.onload = () => ref.current.appendChild(scripts[i + 1])
        }
      })
      // run the first script
      ref.current.appendChild(scripts[0])
    }
    setScriptsInjected(true)
  }, [html, scriptsInjected])

  // On every mount/update scan for embed containers and initialize them
  useEffect(() => {
    renderAllEmbeds(ref.current)
  }, [html, scriptsInjected])

  if (!html) return null

  // Render HTML but replace <script> nodes with spans to prevent React errors
  return (
    <Box ref={ref} {...rest}>
      {HtmlReactParser(html, {
        replace: domNode =>
          domNode.type === "script" && typeof window !== "undefined" ? (
            <span />
          ) : undefined,
      })}
    </Box>
  )
}

CustomHtml.propTypes = {
  html: PropTypes.string,
  ...Box.propTypes,
}

export default CustomHtml
